import React, { useState, useEffect, useRef, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FormField, FieldOption } from '../services/formConfigService';
import * as Icons from 'react-icons/fa';
import formConfigService from '../services/formConfigService';
import webApiFormService from '../services/webApiFormService';
import { WEB_API_BASE_URL } from '../config/config';
import SignaturePad from './SignaturePad';
import AgreementTemplate from './AgreementTemplate';
import MapLocationPicker from './MapLocationPicker';
import BetterBarcodeScanner from './BetterBarcodeScanner';
import RepeatingSection from './RepeatingSection';
import Toast from './Toast';
import PhoneInput from './PhoneInput';
import { 
  groupFieldsByRepeatingSection, 
  createEmptyRow, 
  isSectionVisible,
  RepeatingSection as RepeatingSectionType 
} from '../utils/repeatingSectionUtils';

const FaCalendar = Icons.FaCalendar as any;
const FaCamera = Icons.FaCamera as any;
const FaQrcode = Icons.FaQrcode as any;
const FaTimes = Icons.FaTimes as any;
const FaSpinner = Icons.FaSpinner as any;
const FaMapMarkerAlt = Icons.FaMapMarkerAlt as any;
const FaSearch = Icons.FaSearch as any;
const FaChevronDown = Icons.FaChevronDown as any;
const FaCheck = Icons.FaCheck as any;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const FormSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 8px;
  }
`;

const SectionTitle = styled.h3`
  color: ${props => props.theme.primary};
  font-size: 1.1rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid ${props => props.theme.surface};
`;

const FormGroup = styled.div<{ columnSpan?: number }>`
  margin-bottom: 1.25rem;
  grid-column: span ${props => props.columnSpan || 1};
`;

const FormGrid = styled.div<{ columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.columns || 1}, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.textPrimary};
  font-size: 0.9rem;
  font-weight: 500;
`;

const RequiredMark = styled.span`
  color: ${props => props.theme.error};
  margin-left: 2px;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }

  &:disabled {
    background: ${props => props.theme.surface};
    cursor: not-allowed;
  }

  &.error {
    border-color: ${props => props.theme.error};
  }

  &[type="date"],
  &[type="datetime-local"] {
    cursor: pointer;
    position: relative;
    padding-right: 2.5rem; /* Make room for calendar icon */

    &::-webkit-calendar-picker-indicator {
      cursor: pointer;
      font-size: 1.2rem;
      padding: 0.25rem;
      opacity: 0.7;
      transition: opacity 0.2s;
      position: absolute;
      right: 0.5rem;
      top: 50%;
      transform: translateY(-50%);
    }

    &:hover::-webkit-calendar-picker-indicator {
      opacity: 1;
    }

    &:focus {
      background: white;
      box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
    }

    /* Hide the default calendar icon on mobile and show our custom one */
    @media (max-width: 768px) {
      &::-webkit-calendar-picker-indicator {
        display: none;
      }
    }
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }

  &.error {
    border-color: ${props => props.theme.error};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }

  &.error {
    border-color: ${props => props.theme.error};
  }
`;

// Searchable Select Components
const SearchableSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchableInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 3.5rem; /* Make room for both clear button and dropdown arrow */
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }

  &.error {
    border-color: ${props => props.theme.error};
  }

  &:disabled {
    background: ${props => props.theme.surface};
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  opacity: 0.6;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
    color: ${props => props.theme.error};
  }

  &:disabled {
    display: none;
  }
`;

const DropdownArrowButton = styled.button<{ isOpen: boolean }>`
  position: absolute;
  right: ${props => props.isOpen ? '40px' : '10px'};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary};
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  opacity: 0.7;
  transition: all 0.2s;
  transform: translateY(-50%) ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};

  &:hover {
    opacity: 1;
    color: ${props => props.theme.primary};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const DropdownList = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid ${props => props.theme.border};
  border-top: none;
  border-radius: 0 0 8px 8px;
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const DropdownOption = styled.div<{ isSelected?: boolean; isHighlighted?: boolean }>`
  padding: 0.75rem;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => props.isSelected ? props.theme.primary + '10' :
               props.isHighlighted ? props.theme.surface : 'white'};
  color: ${props => props.isSelected ? props.theme.primary : props.theme.textPrimary};
  font-weight: ${props => props.isSelected ? '600' : '400'};

  &:hover {
    background: ${props => props.theme.surface};
  }
`;

const NoOptionsMessage = styled.div`
  padding: 0.75rem;
  text-align: center;
  color: ${props => props.theme.textSecondary};
  font-style: italic;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RadioWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
`;

const HelpText = styled.p`
  font-size: 0.8rem;
  color: ${props => props.theme.textSecondary};
  margin-top: 0.25rem;
`;

const ErrorText = styled.p`
  font-size: 0.8rem;
  color: ${props => props.theme.error};
  margin-top: 0.25rem;
`;

const InputGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const IconButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${props => props.theme.primaryDark};
    transform: translateY(-50%) scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.textSecondary};
  pointer-events: none;
  font-size: 1.1rem;
  opacity: 0.7;
  transition: opacity 0.2s;

  @media (min-width: 769px) {
    display: none; /* Hide on desktop, use webkit calendar picker */
  }
`;

const LocationWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LocationInput = styled(Input)`
  flex: 1;
`;

const LocationButton = styled.button<{ hasLocation?: boolean; isCapturing?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 1px solid ${props => props.hasLocation ? props.theme.primary : props.theme.border};
  border-radius: 8px;
  background: ${props => props.hasLocation ? props.theme.primary + '10' : 'white'};
  color: ${props => props.hasLocation ? props.theme.primary : props.theme.textPrimary};
  cursor: pointer;
  transition: all 0.3s;
  justify-content: center;
  font-weight: 500;
  font-size: 0.9rem;
  white-space: nowrap;
  min-width: 140px;

  svg {
    font-size: 1rem;
    ${props => props.isCapturing ? css`animation: ${spin} 1s linear infinite;` : ''}
  }

  &:hover:not(:disabled) {
    background: ${props => props.theme.primary};
    color: white;
    border-color: ${props => props.theme.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FileUploadLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 2px dashed ${props => props.theme.border};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  color: ${props => props.theme.textSecondary};

  &:hover {
    border-color: ${props => props.theme.primary};
    color: ${props => props.theme.primary};
    background: ${props => props.theme.surface};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ImagePreviewItem = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid ${props => props.theme.border};
`;

const ImagePreview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0;
  
  &:hover {
    background: rgba(200, 35, 51, 1);
  }
`;

// Full-screen Image Modal Components
const ImageModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 10000;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  border-radius: 8px;
`;

const CloseModalButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0;
  transition: all 0.3s;

  &:hover {
    background: white;
    transform: scale(1.1);
  }
`;

const ClickableImagePreview = styled(ImagePreview)`
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

// TextBoxWithSearch Components
const TextBoxWithSearchWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: stretch;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchButton = styled.button<{ disabled?: boolean; isSearching?: boolean }>`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;
  white-space: nowrap;
  font-weight: 500;
  min-width: 100px;

  &:hover:not(:disabled) {
    background: ${props => props.theme.primaryDark};
    transform: scale(1.02);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg {
    ${props => props.isSearching ? css`animation: ${spin} 1s linear infinite;` : ''}
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SearchInput = styled(Input)`
  flex: 1;
`;

// Scanner Overlay Components (similar to HomePage)
const ScannerOverlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
`;

const ScannerBox = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  width: 100%;
  max-width: 400px;
`;

const ScannerTitle = styled.h3`
  color: ${props => props.theme.primary};
  margin-bottom: 1rem;
  text-align: center;
`;

// H2 Heading Component
const H2Heading = styled.h2`
  color: ${props => props.theme.primary};
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  padding: 0.5rem 0;
  border-bottom: 2px solid ${props => props.theme.primary};
`;

// Paragraph Display Component
const ParagraphDisplay = styled.div`
  color: ${props => props.theme.textPrimary};
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0.5rem 0;
  padding: 1rem;
  background-color: ${props => props.theme.surface};
  border-radius: 4px;
  border-left: 3px solid ${props => props.theme.primary};
  white-space: pre-wrap;
  word-wrap: break-word;
`;

// MultiSelect Components
const MultiSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const MultiSelectInput = styled.div<{ hasError?: boolean; disabled?: boolean }>`
  min-height: 45px;
  padding: 0.5rem;
  border: 1px solid ${props => props.hasError ? props.theme.error : props.theme.border};
  border-radius: 8px;
  background: ${props => props.disabled ? props.theme.surface : 'white'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
  transition: border-color 0.3s;

  &:focus-within {
    border-color: ${props => !props.hasError ? props.theme.primary : props.theme.error};
  }
`;

const SelectedTag = styled.span`
  background: ${props => props.theme.primary};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const RemoveTag = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0;
  font-size: 0.75rem;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
`;

const MultiSelectPlaceholder = styled.span`
  color: ${props => props.theme.textSecondary};
  font-size: 1rem;
  flex: 1;
`;

const MultiSelectDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid ${props => props.theme.border};
  border-top: none;
  border-radius: 0 0 8px 8px;
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const MultiSelectOption = styled.div<{ isSelected?: boolean }>`
  padding: 0.75rem;
  cursor: pointer;
  transition: background 0.2s;
  background: ${props => props.isSelected ? props.theme.primary + '10' : 'white'};
  color: ${props => props.theme.textPrimary};
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: ${props => props.theme.surface};
  }
`;

// MultiSelect Component
interface MultiSelectFieldProps {
  value: string[];
  onChange: (values: string[]) => void;
  options: FieldOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  hasError?: boolean;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  value = [],
  onChange,
  options,
  placeholder = "Select options",
  disabled = false,
  required = false,
  hasError = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleOption = (optionValue: string) => {
    if (disabled) return;

    // Ensure value is an array
    const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
    
    const newValues = valueArray.includes(optionValue)
      ? valueArray.filter(v => v !== optionValue)
      : [...valueArray, optionValue];

    onChange(newValues);
  };

  const handleRemoveTag = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;

    // Ensure value is an array
    const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
    const newValues = valueArray.filter(v => v !== optionValue);
    onChange(newValues);
  };

  const getSelectedOptions = () => {
    // Ensure value is always an array
    const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
    return valueArray.map(val => options.find(opt => opt.value === val)).filter(Boolean) as FieldOption[];
  };

  return (
    <MultiSelectContainer ref={containerRef}>
      <MultiSelectInput
        hasError={hasError}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {getSelectedOptions().map((option) => (
          <SelectedTag key={option.value}>
            {option.text}
            <RemoveTag
              onClick={(e) => handleRemoveTag(option.value, e)}
              type="button"
            >
              ×
            </RemoveTag>
          </SelectedTag>
        ))}
        {(!value || (Array.isArray(value) && value.length === 0)) && (
          <MultiSelectPlaceholder>{placeholder}</MultiSelectPlaceholder>
        )}
        <DropdownArrowButton
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            !disabled && setIsOpen(!isOpen);
          }}
          disabled={disabled}
          isOpen={isOpen}
          title="Toggle dropdown"
          style={{ position: 'absolute', right: '10px' }}
        >
          <FaChevronDown />
        </DropdownArrowButton>
      </MultiSelectInput>

      <MultiSelectDropdown isOpen={isOpen}>
        {options.length > 0 ? (
          options.map((option) => {
            const valueArray = Array.isArray(value) ? value : (value ? [value] : []);
            const isSelected = valueArray.includes(option.value);
            
            return (
              <MultiSelectOption
                key={option.value}
                isSelected={isSelected}
                onClick={() => handleToggleOption(option.value)}
              >
                <Checkbox
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {}} // Handled by parent onClick
                  style={{ pointerEvents: 'none' }}
                />
                {option.text}
              </MultiSelectOption>
            );
          })
        ) : (
          <NoOptionsMessage>No options available</NoOptionsMessage>
        )}
      </MultiSelectDropdown>
    </MultiSelectContainer>
  );
};

// Modern Popup Dropdown Styled Components
const DropdownContainer = styled.div`
  position: relative;
  width: 100%;
`;

const DropdownInput = styled.div<{ disabled?: boolean; hasError?: boolean; isOpen?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid ${props => props.hasError ? '#dc3545' : props.isOpen ? '#007bff' : '#ddd'};
  border-radius: 12px;
  background: ${props => props.disabled ? '#f8f9fa' : 'white'};
  min-height: 48px;
  padding: 0 16px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  box-shadow: ${props => props.isOpen ? '0 4px 12px rgba(0, 123, 255, 0.15)' : '0 2px 4px rgba(0, 0, 0, 0.1)'};

  &:hover {
    border-color: ${props => props.disabled ? '#ddd' : props.hasError ? '#dc3545' : '#007bff'};
    box-shadow: ${props => props.disabled ? 'none' : '0 4px 12px rgba(0, 123, 255, 0.1)'};
  }

  &:focus-within {
    border-color: ${props => props.hasError ? '#dc3545' : '#007bff'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(220, 53, 69, 0.25)' : 'rgba(0, 123, 255, 0.25)'};
  }

  @media (max-width: 768px) {
    min-height: 52px;
    padding: 0 12px;
  }
`;

const InputField = styled.input`
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  color: #333;
  padding: 0;
  
  &::placeholder {
    color: #999;
  }

  &:disabled {
    color: #666;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

const ModernClearButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  border-radius: 50%;
  transition: all 0.2s ease;
  margin-right: 8px;

  &:hover {
    color: #666;
    background-color: #f5f5f5;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModernDropdownArrow = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: transform 0.2s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const ModernDropdownPopup = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 8px;
  backdrop-filter: blur(10px);
  
  /* Mobile optimizations */
  @media (max-width: 768px) {
    max-height: 250px;
    border-radius: 16px;
    margin-top: 4px;
  }
`;

const ModernDropdownOption = styled.div<{ isSelected: boolean; isHighlighted: boolean }>`
  padding: 16px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f5f5f5;
  
  background-color: ${props => {
    if (props.isSelected) return '#e3f2fd';
    if (props.isHighlighted) return '#f8f9fa';
    return 'transparent';
  }};
  
  color: ${props => props.isSelected ? '#1976d2' : '#333'};

  &:hover {
    background-color: ${props => props.isSelected ? '#e3f2fd' : '#f8f9fa'};
  }

  &:last-child {
    border-bottom: none;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 18px 20px;
    min-height: 56px;
  }
`;

const ModernOptionText = styled.span`
  flex: 1;
  font-size: 16px;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ModernCheckIcon = styled.div`
  color: #1976d2;
  font-size: 18px;
  margin-left: 12px;
`;

// Modern Popup Dropdown Component
interface ModernPopupDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: FieldOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  hasError?: boolean;
  fieldName?: string;
  label?: string;
}

const ModernPopupDropdown: React.FC<ModernPopupDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select option",
  disabled = false,
  required = false,
  hasError = false,
  fieldName,
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    return options.filter(option =>
      option.text.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  // Get display value
  const displayValue = useMemo(() => {
    const selectedOption = options.find(option => option.value === value);
    return selectedOption ? selectedOption.text : '';
  }, [options, value]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
    }
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(true);
      setSearchTerm('');
      setHighlightedIndex(-1);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleOptionSelect = (option: FieldOption) => {
    onChange(option.value);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  return (
    <DropdownContainer ref={containerRef}>
      <DropdownInput
        onClick={handleInputClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        hasError={hasError}
        isOpen={isOpen}
      >
        <InputField
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={!isOpen}
          autoComplete="off"
        />
        
        {value && !disabled && (
          <ModernClearButton onClick={handleClear} type="button">
            <FaTimes />
          </ModernClearButton>
        )}
        
        <ModernDropdownArrow isOpen={isOpen}>
          <FaChevronDown />
        </ModernDropdownArrow>
      </DropdownInput>

      {isOpen && (
        <ModernDropdownPopup>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const isSelected = option.value === value;
              const isHighlighted = index === highlightedIndex;
              
              return (
                <ModernDropdownOption
                  key={option.value}
                  isSelected={isSelected}
                  isHighlighted={isHighlighted}
                  onClick={() => handleOptionSelect(option)}
                >
                  <ModernOptionText>{option.text}</ModernOptionText>
                  {isSelected && <ModernCheckIcon><FaCheck /></ModernCheckIcon>}
                </ModernDropdownOption>
              );
            })
          ) : (
            <NoOptionsMessage>No options found</NoOptionsMessage>
          )}
        </ModernDropdownPopup>
      )}
    </DropdownContainer>
  );
};

// Searchable Select Component (COMMENTED OUT - REPLACED WITH ModernPopupDropdown)
/*
interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: FieldOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  hasError?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select option",
  disabled = false,
  required = false,
  hasError = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selectedOption = options.find(opt => opt.value === value);
    setDisplayValue(selectedOption ? selectedOption.text : '');
    setSearchTerm('');
  }, [value, options]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    return options.filter(option =>
      option.text.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        const selectedOption = options.find(opt => opt.value === value);
        setDisplayValue(selectedOption ? selectedOption.text : '');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, options]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(prev =>
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(prev =>
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        } else if (!isOpen) {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        const selectedOption = options.find(opt => opt.value === value);
        setDisplayValue(selectedOption ? selectedOption.text : '');
        break;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);
    setSearchTerm(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
      setSearchTerm(displayValue);
      setDisplayValue('');
    }
  };

  const handleOptionSelect = (option: FieldOption) => {
    onChange(option.value);
    setDisplayValue(option.text);
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setDisplayValue('');
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <SearchableSelectContainer ref={containerRef}>
      <SearchableInput
        type="text"
        value={isOpen ? searchTerm : displayValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        className={hasError ? 'error' : ''}
        autoComplete="off"
      />

      <DropdownArrowButton
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        isOpen={isOpen}
        title="Toggle dropdown"
      >
        <FaChevronDown />
      </DropdownArrowButton>

      {value && !disabled && (
        <ClearButton
          type="button"
          onClick={handleClear}
          title="Clear selection"
        >
          <FaTimes />
        </ClearButton>
      )}

      <DropdownList isOpen={isOpen}>
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, index) => (
            <DropdownOption
              key={option.value}
              isSelected={option.value === value}
              isHighlighted={index === highlightedIndex}
              onClick={() => handleOptionSelect(option)}
            >
              {option.text}
            </DropdownOption>
          ))
        ) : (
          <NoOptionsMessage>
            {searchTerm ? 'No options found' : 'No options available'}
          </NoOptionsMessage>
        )}
      </DropdownList>
    </SearchableSelectContainer>
  );
};
*/

interface DynamicFormRendererProps {
  fields: FormField[];
  sections: Map<string, FormField[]>;
  formData: any;
  onChange: (fieldName: string, value: any) => void;
  errors?: Record<string, string>;
  onScan?: (fieldName: string, value: string) => void;
  onLocationCapture?: (latitude: number, longitude: number) => void;
  autoTriggerSearchField?: string;
}

const DynamicFormRenderer: React.FC<DynamicFormRendererProps> = ({
  fields,
  sections,
  formData,
  onChange,
  errors = {},
  onScan,
  onLocationCapture,
  autoTriggerSearchField
}) => {
  const [dropdownOptions, setDropdownOptions] = useState<Record<string, FieldOption[]>>({});
  const [loadingFields, setLoadingFields] = useState<Set<string>>(new Set());
  const [scanningField, setScanningField] = useState<string | null>(null);
  const lastResolvedUrls = useRef<Map<string, string>>(new Map());
  const [isBindingSerialNumber, setIsBindingSerialNumber] = useState(false);

  // Image modal state
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // TextBoxWithSearch state
  const [searchingFields, setSearchingFields] = useState<Set<string>>(new Set());
  const apiResponseCache = useRef<Map<string, any>>(new Map());
  const [capturingLocation, setCapturingLocation] = useState<Set<string>>(new Set());

  // Repeating Sections state
  const [repeatingSectionData, setRepeatingSectionData] = useState<{ [sectionId: string]: any[] }>({});
  const repeatingSections = useMemo(() => groupFieldsByRepeatingSection(fields), [fields]);

  // Local validation errors state (for real-time validation)
  const [localValidationErrors, setLocalValidationErrors] = useState<Record<string, string>>({});

  // Merge passed-in errors with local validation errors (local takes precedence, avoid duplicates)
  const mergedErrors = useMemo(() => {
    const merged: Record<string, string> = {};
    // Add errors from parent (only if not already in local)
    Object.keys(errors).forEach(key => {
      if (!localValidationErrors[key]) {
        merged[key] = errors[key];
      }
    });
    // Add local validation errors (these take precedence)
    Object.keys(localValidationErrors).forEach(key => {
      merged[key] = localValidationErrors[key];
    });
    return merged;
  }, [errors, localValidationErrors]);

  // Validate field on change and call parent onChange
  const handleFieldChange = (fieldName: string, value: any) => {
    // Find the field configuration
    const field = fields.find(f => f.fieldName === fieldName);

    if (field) {
      // Block invalid input for fields with a validation pattern
      if (field.validationPattern && value && typeof value === 'string' && value.length > 0) {
        const pattern = new RegExp(field.validationPattern);
        if (!pattern.test(value)) {
          // Reject the input — don't update the value
          setLocalValidationErrors(prev => ({
            ...prev,
            [fieldName]: field.errorMessage || `${field.label} format is invalid`
          }));
          return;
        }
      }

      // Block invalid input for email fields
      // (only show error, don't block typing — email is validated on blur/submit)

      // Validate the field
      const validation = formConfigService.validateField(field, value);

      // Update local validation errors
      setLocalValidationErrors(prev => {
        const newErrors = { ...prev };
        if (!validation.isValid && validation.errorMessage) {
          newErrors[fieldName] = validation.errorMessage;
        } else {
          delete newErrors[fieldName];
        }
        return newErrors;
      });
    }

    // Call the parent onChange
    onChange(fieldName, value);
  };

  // Toast notification state
  const [toast, setToast] = useState<{ isVisible: boolean; message: string; type: 'loading' | 'success' }>({
    isVisible: false,
    message: '',
    type: 'loading'
  });

  // Track if we've already triggered onChange APIs for initial load
  const hasTriggeredInitialApis = useRef(false);

  // Listen for custom trigger events from parent
  useEffect(() => {
    const handleTrigger = (event: any) => {
      const { fieldName, value } = event.detail;
      console.log(`📡 Received triggerFieldOnChange for ${fieldName} with value: ${value}`);
      
      const field = fields.find(f => f.fieldName === fieldName);
      if (field && field.onChangeApiEndpoint1) {
        if (field.onChangeApiCount && field.onChangeApiCount > 1) {
          performMultipleSearchApiCalls(field, value);
        } else {
          performSearchApiCall(field, value);
        }
      }
    };

    window.addEventListener('triggerFieldOnChange', handleTrigger);
    return () => window.removeEventListener('triggerFieldOnChange', handleTrigger);
  }, [fields]);

  // Auto-trigger onChange APIs for fields with {search_value} when form data is loaded
  // Only runs ONCE on initial load — not on subsequent keystrokes
  useEffect(() => {
    // Only trigger once when formData has values and fields are loaded
    if (hasTriggeredInitialApis.current || !fields || fields.length === 0 || !formData || Object.keys(formData).length === 0) {
      return;
    }

    console.log('🔄 Checking for auto-trigger fields on form load...');

    // Find fields that have {search_value} in their onChange API endpoints
    const fieldsToTrigger = fields.filter(field => {
      const fieldNameLower = field.fieldName.toLowerCase();
      
      // If autoTriggerSearchField is explicitly provided, allow it even if it's a serial field
      const isExplicitlyRequested = autoTriggerSearchField && (
        field.fieldName === autoTriggerSearchField || 
        fieldNameLower === autoTriggerSearchField.toLowerCase()
      );

      // Skip serial number / scanner fields UNLESS explicitly requested
      const isSerialField = fieldNameLower.includes('serial') || fieldNameLower.includes('scanner');
      if (isSerialField && !isExplicitlyRequested) return false;

      const hasOnChangeApi = field.onChangeApiEndpoint1 && field.onChangeApiEndpoint1.trim() !== '';
      const hasValue = formData[field.fieldName] && formData[field.fieldName] !== '';
      const hasSearchValuePlaceholder = hasOnChangeApi && field.onChangeApiEndpoint1?.toLowerCase().includes('{search_value}');

      return (hasOnChangeApi && hasValue && hasSearchValuePlaceholder) || isExplicitlyRequested;
    });

    if (fieldsToTrigger.length > 0) {
      console.log(`✅ Found ${fieldsToTrigger.length} fields to auto-trigger:`, fieldsToTrigger.map(f => f.fieldName));
      hasTriggeredInitialApis.current = true;

      // Trigger after a small delay to ensure component is fully mounted
      setTimeout(() => {
        fieldsToTrigger.forEach(field => {
          const value = formData[field.fieldName];
          if (value) {
            console.log(`🚀 Auto-triggering onChange for ${field.fieldName} with value: ${value}`);
            if (field.onChangeApiCount && field.onChangeApiCount > 1) {
              performMultipleSearchApiCalls(field, value);
            } else {
              performSearchApiCall(field, value);
            }
          }
        });
      }, 300);
    }
  }, [formData, fields, autoTriggerSearchField]);

  // Handle serial number binding for equipment fields
  const handleSerialNumberBinding = async (serialNumber: string, fieldName: string) => {
    // Check if this is a serial number field (not the module name)
    const isSerialNumberField =
      fieldName === 'Serial_Number' ||
      fieldName === 'serial_no_scanner' ||
      fieldName === 'Serial_No_Scanner' ||
      fieldName === 'cdesc_serial_no' ||
      fieldName.toLowerCase().includes('serial') && fieldName.toLowerCase().includes('number');

    if (!serialNumber?.trim() || !isSerialNumberField) {
      return;
    }

    try {
      console.log(`Binding equipment data for serial number: ${serialNumber}`);
      setIsBindingSerialNumber(true);

      // Use the correct module name for the API
      const result = await webApiFormService.bindEquipmentBySerialNumber(
        serialNumber.trim(),
        'EquipmentMaster-SerialNumber'
      );

      if (result.success && result.data) {

        // Handle array response from API - take first item if array
        const equipmentData = Array.isArray(result.data) && result.data.length > 0
          ? result.data[0]
          : result.data;

        if (!equipmentData || (Array.isArray(result.data) && result.data.length === 0)) {
          alert('No equipment found for serial number: ' + serialNumber);
          setIsBindingSerialNumber(false);
          return;
        }


        const updateData: Record<string, any> = {};

        // Map fields from EquipmentMaster-SerialNumber-Customer-Detail API response
        // Include variations for field names with dots, spaces, underscores
        const fieldMappings: Record<string, string[]> = {
          // Common variations for Manufacturer Serial Number - map to actual serial number from equipment
          'Manuf. Serial No.': ['serialNumber'],  // This should be the serial number itself
          'Manuf._Serial_No.': ['serialNumber'],
          'Manufacturer_Serial_Number': ['serialNumber'],
          'ManufacturerSerialNumber': ['serialNumber'],
          'Manufacturer Serial Number': ['serialNumber'],

          // Common variations for Model Number
          'Model No.': ['modelNumber'],
          'Model_No.': ['modelNumber'],
          'Model_Number': ['modelNumber'],
          'ModelNumber': ['modelNumber'],
          'Model Number': ['modelNumber'],

          // Customer/Store Details
          'Customer-CustomerNumber': ['StoreNumber', 'CustomerNumber', 'Customer_Number', 'ship2party'],
          'Customer-Name': ['StoreName', 'CustomerName', 'Customer_Name', 'ship2party_name'],
          'Customer-OwnerName': ['OwnerName', 'Owner_Name'],
          'Customer-Address': ['OutletAddress', 'CustomerAddress', 'Customer_Address'],
          'Customer-HomeAddress': ['HomeAddress', 'Home_Address'],
          'Customer-Province': ['Province', 'ProvinceName'],
          'Customer-City': ['City', 'CityName'],
          'Customer-PostalCode': ['PostalCode', 'Postal_Code'],
          'Customer-GeoLocation': ['GeoLocation', 'Geo_Location'],
          'Customer-Channel': ['channel', 'Channel'],
          'Customer-Segmentation': ['Segmentation', 'Customer_Segmentation'],
          'Customer-RouteNumber': ['RouteNumber', 'Route_Number'],
          'Customer-Route': ['Route', 'RouteCode'],
          'Customer-MotherCode': ['MotherCode', 'Mother_Code'],
          'Customer-MotherName': ['MotherName', 'Mother_Name'],

          // Sales/Operational Details
          'Sales-AverageMonthlyVolume': ['AverageMonthlyVolume', 'Average_Monthly_Volume'],
          'Sales-Preseller': ['Preseller', 'PresellerName'],
          'Sales-TSM': ['TSM', 'TSMName'],
          'Sales-Region': ['SalesRegion', 'Sales_Region'],
          'Sales-CU': ['CU', 'CustomerUnit'],

          // Plant/Warehouse Details
          'Plant-DELOC': ['DELOC', 'DeliveryLocation'],
          'Plant-Code': ['Plant', 'PlantCode'],
          'Plant-SAPCO2Inventory': ['SAPCO2Inventory', 'SAP_CO2_Inventory'],

          // CDE Details
          'CDE-WithMissingRecord': ['WithMissingCDERecord', 'With_Missing_CDE_Record'],
          'CDE-RecordQuantity': ['CustomerCDERecordQty', 'Customer_CDE_Record_Qty'],
          'CDE-Assigned': ['CDEAAssigned', 'CDEA_Assigned'],
          'CDE-AvailableInCallCycle': ['IsAvailableInCallCycle', 'Is_Available_In_Call_Cycle'],

          // TPSP Details
          'TPSP-Assigned': ['TPSPAssigned', 'TPSP_Assigned'],
          'TPSP-WarehouseAdmin': ['TPSPWarehouseAdmin', 'TPSP_Warehouse_Admin'],

          // Additional fields that might be in the form
          'ProvinceUID': ['ProvinceUID'],
          'CityUID': ['CityUID'],
          'PostalCodeUID': ['PostalCodeUID']
        };

        // Bind data to matching fields
        Object.entries(fieldMappings).forEach(([formFieldName, apiFieldNames]) => {
          // Check if this field exists in our form
          const formField = fields.find(f =>
            f.fieldName === formFieldName ||
            f.label === formFieldName ||
            f.fieldName.toLowerCase() === formFieldName.toLowerCase()
          );

          if (formField) {
            // Find the first matching API field that has data
            for (const apiFieldName of apiFieldNames) {
              if (equipmentData[apiFieldName] !== undefined && equipmentData[apiFieldName] !== null && equipmentData[apiFieldName] !== '') {
                updateData[formField.fieldName] = equipmentData[apiFieldName];
                break;
              }
            }
          }
        });

        // Also bind any direct field matches (case-insensitive)
        // Check both fieldName and label for matches
        Object.keys(equipmentData).forEach(apiKey => {
          const matchingField = fields.find(field => {
            const fieldNameLower = field.fieldName.toLowerCase();
            const labelLower = (field.label || '').toLowerCase();
            const apiKeyLower = apiKey.toLowerCase();

            // Skip fields that are not configured as API-populated to prevent false matches
            if (!field.isApiPopulatedColumn) return false;

            return (
              fieldNameLower === apiKeyLower ||
              labelLower === apiKeyLower ||
              fieldNameLower.includes(apiKeyLower) ||
              labelLower.includes(apiKeyLower) ||
              apiKeyLower.includes(fieldNameLower)
            );
          });

          if (matchingField && !updateData[matchingField.fieldName]) {
            updateData[matchingField.fieldName] = equipmentData[apiKey];
          }
        });

        // Special handling for common form fields that might not match exactly
        fields.forEach(field => {
          if (!updateData[field.fieldName]) {
            // Check for Manufacturer Serial Number variations - use manufact_serial_number from API
            if ((field.label?.toLowerCase().includes('manuf') && field.label?.toLowerCase().includes('serial')) ||
                field.fieldName === 'Manufacturer_Serial_Number') {
              // Use manufact_serial_number from API response
              updateData[field.fieldName] = equipmentData.manufact_serial_number || '';
            }
            // Check for Model Number fields
            else if ((field.label?.toLowerCase().includes('model') && (field.label?.toLowerCase().includes('no') || field.label?.toLowerCase().includes('number'))) ||
                field.fieldName === 'Model_Number') {
              // Use model_no from API response
              updateData[field.fieldName] = equipmentData.model_no || '';
            }
            // Check for Customer Number fields
            else if (field.fieldName === 'Customer_Number' || field.fieldName === 'Customer-Number') {
              updateData[field.fieldName] = equipmentData.ship2party || '';
            }
            // Check for Customer Name fields
            else if (field.fieldName === 'Customer_Name' || field.fieldName === 'Outlet_Store_Name') {
              updateData[field.fieldName] = equipmentData.ship2party_name || '';
            }
            // Check for Owner Name fields - not available in equipment response
            else if (field.fieldName === 'Owner_Name') {
              // Owner name not available in equipment API - would need customer detail API
              updateData[field.fieldName] = '';
            }
            // Check for Description fields
            else if (field.fieldName === 'Equipment_Description' || field.fieldName === 'Description') {
              updateData[field.fieldName] = equipmentData.description || '';
            }
          }
        });

        // Update form data with bound values
        if (Object.keys(updateData).length > 0) {
          Object.entries(updateData).forEach(([fieldName, value]) => {
            onChange(fieldName, value);
          });

          // Auto-trigger search for Customer_Number field if it was populated
          const customerNumberField = fields.find(f => 
            f.fieldName === 'Customer_Number' || 
            f.fieldName === 'Customer-Number' ||
            f.fieldName === 'customer_number'
          );

          if (customerNumberField && updateData[customerNumberField.fieldName]) {
            const customerNumber = updateData[customerNumberField.fieldName];
            
            // Trigger search after a short delay to ensure form data is updated
            setTimeout(() => {
              if (customerNumberField.onChangeApiEndpoint1) {
                performSearchApiCall(customerNumberField, customerNumber);
              }
            }, 500);
          }
        } else {
        }
      } else {
      }
    } catch (error) {
    } finally {
      setIsBindingSerialNumber(false);
    }
  };

  // Load dropdown options for API-based fields with dependency support
  useEffect(() => {
    const loadDropdownOptions = async () => {

      // Process all dropdown fields including multiselect
      const dropdownFields = fields.filter(field =>
        field.fieldType === 'dropdown' ||
        field.fieldType === 'dropdownpopup' ||
        field.fieldType === 'select' ||
        field.fieldType === 'radio' ||
        field.fieldType === 'multiselect'
      );

      for (const field of dropdownFields) {
        // Handle static options first
        if (field.sourceType === 'static' || field.staticOptions) {
          if (!dropdownOptions[field.fieldName]) {
            const staticOpts = Array.isArray(field.staticOptions) ? field.staticOptions : [];
            setDropdownOptions(prev => ({
              ...prev,
              [field.fieldName]: staticOpts
            }));
          }
          continue;
        }

        // Handle API-based options (including indexeddb source type)
        if ((field.sourceType === 'api' || field.sourceType === 'indexeddb') && field.dataSource) {
          // Skip reloading these specific fields if they already have data to prevent continuous re-rendering
          const problematicFields = ['preseller', 'tsm', 'territory_sales_manager', 'cdea_assigned', 'tpsp_assigned'];
          const fieldNameLower = field.fieldName.toLowerCase();

          // Check if this is one of the problematic fields and already has data
          if (problematicFields.some(pf => fieldNameLower.includes(pf))) {
            if (dropdownOptions[field.fieldName] && dropdownOptions[field.fieldName].length > 0) {
              continue; // Skip reloading this field as it already has data
            }
          }

          let shouldReload = false;
          let resolvedDataSource = field.dataSource;

          // Replace field placeholders with actual values from form data
          const placeholderRegex = /\{([^}]+)\}/g;
          const dependencies: string[] = [];
          let hasPlaceholders = false;

          // Check if dataSource has placeholders
          const matches = field.dataSource.match(placeholderRegex);
          if (matches) {
            hasPlaceholders = true;
            resolvedDataSource = field.dataSource.replace(placeholderRegex, (match: string, fieldName: string) => {
              dependencies.push(fieldName);
              
              // Try to find the field value - check both exact match and with cv_ prefix
              let fieldValue = formData[fieldName];
              
              // If not found and doesn't start with cv_, try with cv_ prefix
              if (!fieldValue && !fieldName.startsWith('cv_')) {
                fieldValue = formData[`cv_${fieldName}`];
              }
              
              // If still not found and starts with cv_, try without prefix
              if (!fieldValue && fieldName.startsWith('cv_')) {
                fieldValue = formData[fieldName.replace('cv_', '')];
              }
              
              return encodeURIComponent(fieldValue || '');
            });
          }

          // Determine if we should reload
          if (hasPlaceholders) {
            // Only load if all dependencies have non-empty values
            shouldReload = dependencies.every(dep => {
              // Check both exact match and with/without cv_ prefix
              let value = formData[dep];
              
              if (!value && !dep.startsWith('cv_')) {
                value = formData[`cv_${dep}`];
              }
              
              if (!value && dep.startsWith('cv_')) {
                value = formData[dep.replace('cv_', '')];
              }
              
              return value !== undefined && value !== null && value !== '';
            });


            if (!shouldReload) {
              // Clear options if dependencies are not satisfied
              setDropdownOptions(prev => ({
                ...prev,
                [field.fieldName]: []
              }));
              continue;
            }
          } else {
            // For fields without dependencies, load once
            shouldReload = !dropdownOptions[field.fieldName];
          }

          // For dependent fields, also reload if dependency values changed since last load
          if (hasPlaceholders && dropdownOptions[field.fieldName]) {
            // Check if any dependency values have changed by comparing with stored URLs
            const currentResolvedUrl = resolvedDataSource;
            const storedUrl = lastResolvedUrls.current.get(field.fieldName);
            if (storedUrl && storedUrl !== currentResolvedUrl) {
              shouldReload = true;
            }
            lastResolvedUrls.current.set(field.fieldName, currentResolvedUrl);
          }

          if (shouldReload) {
            setLoadingFields(prev => new Set(prev).add(field.fieldName));
            try {

              // Call through MobileService proxy to avoid CORS issues
              const authToken = localStorage.getItem('authToken');
              const jobPositionUID = localStorage.getItem('jobPositionUID') || '';
              const orgUID = localStorage.getItem('orgUID') || '';

              // Build the proxy URL
              let apiPath = resolvedDataSource;
              if (!apiPath.startsWith('/')) {
                apiPath = '/' + apiPath;
              }

              // Add JobPositionUID and OrgUID to the path if needed
              if (apiPath.includes('GetListDataByModule')) {
                const separator = apiPath.includes('?') ? '&' : '?';
                apiPath = `${apiPath}${separator}JobPositionUID=${jobPositionUID}&OrgUID=${orgUID}`;
              }

              // Call Web API directly
              const directUrl = `${WEB_API_BASE_URL}${apiPath}`;

              const headers: HeadersInit = {
                'Content-Type': 'application/json'
              };
              if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
              }

              const response = await fetch(directUrl, {
                method: 'GET',
                headers
              });

              if (!response.ok) {
                console.error(`❌ Dropdown API Error for ${field.fieldName}:`, response.status);
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              const data = await response.json();
              let options: FieldOption[] = [];

              // Parse the response to extract options
              if (Array.isArray(data)) {
                options = data.map((item: any) => ({
                  value: item.value || item.Value || item.code || item.Code || item.id || item.ID || item.uid || item.UID || '',
                  text: item.text || item.Text || item.label || item.Label || item.name || item.Name || item.description || item.value || ''
                }));
              } else if (data.Data && data.Data.ModelData && Array.isArray(data.Data.ModelData)) {
                // Handle Web API response format: { Data: { ModelData: [...] } }
                options = data.Data.ModelData.map((item: any) => ({
                  value: item.code || item.Code || item.value || item.Value || item.id || item.ID || item.uid || item.UID || '',
                  text: item.name || item.Name || item.text || item.Text || item.label || item.Label || item.description || item.value || ''
                }));
              } else if (data.data && Array.isArray(data.data)) {
                options = data.data.map((item: any) => ({
                  value: item.value || item.Value || item.code || item.Code || item.id || item.ID || item.uid || item.UID || '',
                  text: item.text || item.Text || item.label || item.Label || item.name || item.Name || item.description || item.value || ''
                }));
              } else if (data.items && Array.isArray(data.items)) {
                options = data.items.map((item: any) => ({
                  value: item.value || item.Value || item.code || item.Code || item.id || item.ID || item.uid || item.UID || '',
                  text: item.text || item.Text || item.label || item.Label || item.name || item.Name || item.description || item.value || ''
                }));
              }

              setDropdownOptions(prev => ({
                ...prev,
                [field.fieldName]: options
              }));
            } catch (error) {
              console.error(`❌ Error loading dropdown ${field.fieldName}:`, error);
              setDropdownOptions(prev => ({
                ...prev,
                [field.fieldName]: []
              }));
            } finally {
              setLoadingFields(prev => {
                const newSet = new Set(prev);
                newSet.delete(field.fieldName);
                return newSet;
              });
            }
          }
        }
      }
    };

    loadDropdownOptions();
  }, [fields, formData]); // Added formData back for dependent dropdowns to work

  // Track previous dependency values to detect actual changes
  const prevDependencyValues = useRef<Map<string, any>>(new Map());

  // Auto-trigger search for Customer Number field if it has a value on load
  const hasTriggeredInitialSearch = useRef(false);
  
  useEffect(() => {
    // Only trigger once on initial load
    if (hasTriggeredInitialSearch.current) return;
    
    // Wait for fields to be loaded (fields array should not be empty)
    if (!fields || fields.length === 0) return;
    
    // First try to find by field name
    let customerNumberField = fields.find(f => 
      f.fieldName === 'Customer_Number' || 
      f.fieldName === 'Customer-Number' ||
      f.fieldName === 'customer_number' ||
      f.fieldName === 'cdesc_customer_number'
    );

    // If found but doesn't have API config, search for any field with customer-related API endpoint
    if (customerNumberField && !customerNumberField.isOnChangeFetchDataFromAPI) {
      const fieldWithCustomerApi = fields.find(f => 
        f.isOnChangeFetchDataFromAPI && 
        f.onChangeApiEndpoint1 && 
        (f.onChangeApiEndpoint1.toLowerCase().includes('customer') ||
         f.fieldName.toLowerCase().includes('customer'))
      );
      
      if (fieldWithCustomerApi) {
        customerNumberField = fieldWithCustomerApi;
      }
    }

    // Ensure field is fully configured with API settings
    // Only check if onChangeApiEndpoint1 exists (same as manual search button)
    if (customerNumberField && 
        formData[customerNumberField.fieldName] && 
        customerNumberField.onChangeApiEndpoint1) {
      
      const customerNumber = formData[customerNumberField.fieldName];
      
      // Trigger search after 1 second delay to ensure everything is loaded
      setTimeout(() => {
        performSearchApiCall(customerNumberField!, customerNumber);
        hasTriggeredInitialSearch.current = true;
      }, 1000);
    }
  }, [fields, formData]);

  // Initialize date fields with today's date if no value is set
  useEffect(() => {
    const dateFields = fields.filter(field => 
      (field.fieldType === 'date' || field.fieldType === 'datetime' || field.fieldType === 'datetime-local') &&
      !formData[field.fieldName] && !field.defaultValue
    );

    if (dateFields.length > 0) {
      dateFields.forEach(field => {
        const todayValue = getTodayDate(field.fieldType);
        if (todayValue) {
          onChange(field.fieldName, todayValue);
        }
      });
    }
  }, [fields, formData]); // Run when fields or formData changes

  // Clear dependent field values when their dependencies change
  useEffect(() => {
    const fieldsWithDependencies = fields.filter(field =>
      (field.fieldType === 'dropdown' || field.fieldType === 'dropdownpopup' || field.fieldType === 'select' || field.fieldType === 'multiselect') &&
      field.sourceType === 'api' &&
      field.dataSource &&
      field.dataSource.includes('{')
    );

    fieldsWithDependencies.forEach(field => {
      // Extract dependencies from dataSource
      const placeholderRegex = /\{([^}]+)\}/g;
      const matches = field.dataSource!.match(placeholderRegex);

      if (matches) {
        const dependencies = matches.map(match => match.slice(1, -1)); // Remove { and }

        // Check if any dependency value has actually CHANGED
        dependencies.forEach(dep => {
          // Try to get dependency value with cv_ prefix matching
          let depValue = formData[dep];
          if (!depValue && !dep.startsWith('cv_')) {
            depValue = formData[`cv_${dep}`];
          }
          if (!depValue && dep.startsWith('cv_')) {
            depValue = formData[dep.replace('cv_', '')];
          }

          const prevValue = prevDependencyValues.current.get(`${field.fieldName}_${dep}`);
          
          // Only clear if dependency value actually changed (not just initial load)
          if (prevValue !== undefined && prevValue !== depValue && formData[field.fieldName]) {
            console.log(`Clearing ${field.fieldName} because ${dep} changed from ${prevValue} to ${depValue}`);
            onChange(field.fieldName, field.fieldType === 'multiselect' ? [] : '');
          }
          
          // Store current value
          prevDependencyValues.current.set(`${field.fieldName}_${dep}`, depValue);
        });
      }
    });
  }, [formData, fields, onChange]);

  // Log when component mounts to debug dropdown loading
  useEffect(() => {
  }, [fields]);

  const handleLocationCapture = (fieldName: string) => {
    if (navigator.geolocation) {
      setCapturingLocation(prev => new Set(prev).add(fieldName));

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const locationString = `${latitude}, ${longitude}`;
          onChange(fieldName, locationString);

          // Also try to update individual latitude/longitude fields if they exist
          onChange('Latitude', latitude.toString());
          onChange('Longitude', longitude.toString());

          if (onLocationCapture) {
            onLocationCapture(latitude, longitude);
          }

          setCapturingLocation(prev => {
            const newSet = new Set(prev);
            newSet.delete(fieldName);
            return newSet;
          });
        },
        (error) => {
          console.error('Geolocation error:', error);
          // Silently fail - no alert popup
          setCapturingLocation(prev => {
            const newSet = new Set(prev);
            newSet.delete(fieldName);
            return newSet;
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    } else {
      console.error('Geolocation is not supported by this browser');
      // Silently fail - no alert popup
    }
  };

  const startScanning = (fieldName: string) => {
    setScanningField(fieldName);
  };

  const stopScanning = () => {
    setScanningField(null);
  };

  // ============================================
  // TextBoxWithSearch Helper Functions
  // ============================================

  /**
   * Extract property value from object using nested path (e.g., "Customer.Address.City")
   */
  const extractPropertyValue = (obj: any, path: string): any => {
    if (!obj || !path) return null;

    const parts = path.split('.');
    let current = obj;

    for (const part of parts) {
      if (current && typeof current === 'object' && current[part] !== undefined) {
        current = current[part];
      } else {
        return null;
      }
    }

    return current;
  };

  /**
   * Find all fields that should be populated by a search field
   */
  const findFieldsToPopulate = (searchField: FormField): FormField[] => {
    const fieldsToPopulate = fields.filter(f =>
      f.apiPopulatedColumnParentFieldId === searchField.fieldName
    );

    // console.log(`Fields configured to populate from ${searchField.fieldName}:`,
    //   fieldsToPopulate.map(f => ({
    //     fieldName: f.fieldName,
    //     label: f.label,
    //     propertyName: f.apiPopulatedColumnParentFieldIdPropertyName
    //   }))
    // );

    return fieldsToPopulate;
  };

  /**
   * Process API response and populate dependent fields
   */
  const processSearchApiResponse = (searchField: FormField, response: any) => {
    try {
      // Extract data object - handle ModelData wrapper
      let dataObject = response?.Data || response?.data || response;

      // If the data is wrapped in ModelData, unwrap it
      if (dataObject?.ModelData) {
        dataObject = dataObject.ModelData;
      }

      // Find all fields to populate
      const fieldsToPopulate = findFieldsToPopulate(searchField);

      // Cache the API response
      apiResponseCache.current.set(searchField.fieldName, dataObject);

      // Separate dropdown fields from regular value fields
      const dropdownFields = fieldsToPopulate.filter(f =>
        f.fieldType === 'dropdown' || f.fieldType === 'select' || f.fieldType === 'multiselect'
      );
      const regularFields = fieldsToPopulate.filter(f =>
        f.fieldType !== 'dropdown' && f.fieldType !== 'select' && f.fieldType !== 'multiselect'
      );

      // Handle dropdown fields - these receive UID values, not arrays
      dropdownFields.forEach(field => {
        if (field.apiPopulatedColumnParentFieldIdPropertyName) {
          const value = extractPropertyValue(
            dataObject,
            field.apiPopulatedColumnParentFieldIdPropertyName
          );

          // For dropdowns, the API returns the UID value directly, not an array
          // We just set the value - the dropdown should already have options loaded from its API
          if (value !== null && value !== undefined) {
            // console.log(`Populating dropdown ${field.fieldName} (${field.label}) with UID:`, value);
            onChange(field.fieldName, value);
          } else {
            // console.log(`No value found for dropdown ${field.fieldName} (${field.label}), property: ${field.apiPopulatedColumnParentFieldIdPropertyName}`);
          }
        }
      });

      // Populate regular field values
      regularFields.forEach(field => {
        if (field.apiPopulatedColumnParentFieldIdPropertyName) {
          const value = extractPropertyValue(
            dataObject,
            field.apiPopulatedColumnParentFieldIdPropertyName
          );

          if (value !== null && value !== undefined) {
            onChange(field.fieldName, value);
          }
        }
      });

    } catch (error) {
      console.error('Error processing search API response:', error);
    }
  };

  /**
   * Process multiple API responses (when onChangeApiCount > 1)
   */
  const processMultipleSearchApiResponses = (searchField: FormField, responses: any[]) => {
    try {
      console.log(`Processing ${responses.length} API responses for ${searchField.fieldName}`);

      const fieldsToPopulate = findFieldsToPopulate(searchField);

      fieldsToPopulate.forEach(field => {
        const apiNumber = field.apiNumber || 1;
        const apiIndex = apiNumber - 1;

        if (apiIndex >= 0 && apiIndex < responses.length) {
          const response = responses[apiIndex];
          const dataObject = response?.Data || response?.data || response;

          if (field.apiPopulatedColumnParentFieldIdPropertyName) {
            const value = extractPropertyValue(
              dataObject,
              field.apiPopulatedColumnParentFieldIdPropertyName
            );

            if (value !== null && value !== undefined) {
              if (field.fieldType === 'dropdown' || field.fieldType === 'dropdownpopup' || field.fieldType === 'select' || field.fieldType === 'multiselect') {
                // Handle dropdown options
                if (Array.isArray(value)) {
                  setDropdownOptions(prev => ({
                    ...prev,
                    [field.fieldName]: value.map(item => ({
                      value: item.value || item.code || item.Code || item.id || '',
                      text: item.text || item.name || item.Name || item.description || ''
                    }))
                  }));
                }
              } else {
                // Handle regular field value
                onChange(field.fieldName, value);
              }
            }
          }
        }
      });

    } catch (error) {
      console.error('Error processing multiple search API responses:', error);
    }
  };

  /**
   * Perform single API call for TextBoxWithSearch
   */
  const performSearchApiCall = async (field: FormField, searchValue: string) => {
    const apiEndpoint = field.onChangeApiEndpoint1;
    if (!apiEndpoint) return;

    try {
      setSearchingFields(prev => new Set(prev).add(field.fieldName));
      
      // Show loading toast
      setToast({
        isVisible: true,
        message: 'Loading customer details...',
        type: 'loading'
      });

      // Replace {value} or {search_value} placeholder with actual search value
      let url = apiEndpoint.replace(/{value}/g, encodeURIComponent(searchValue));
      url = url.replace(/{search_value}/g, encodeURIComponent(searchValue));

      // Call Web API
      const authToken = localStorage.getItem('authToken');
      const fullUrl = url.startsWith('http') ? url : `${WEB_API_BASE_URL}${url.startsWith('/') ? url : '/' + url}`;

      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      processSearchApiResponse(field, data);
      
      // Show success toast
      setToast({
        isVisible: true,
        message: 'Customer details loaded!',
        type: 'success'
      });

    } catch (error) {
      console.error('Error in search API call:', error);
      // Hide toast on error
      setToast({ isVisible: false, message: '', type: 'loading' });
      alert('Search failed. Please try again.');
    } finally {
      setSearchingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(field.fieldName);
        return newSet;
      });
    }
  };

  /**
   * Perform multiple parallel API calls for TextBoxWithSearch
   */
  const performMultipleSearchApiCalls = async (field: FormField, searchValue: string) => {
    const apiCount = field.onChangeApiCount || 1;
    const apiCalls: Promise<any>[] = [];

    try {
      setSearchingFields(prev => new Set(prev).add(field.fieldName));

      // Build array of API calls
      for (let i = 1; i <= apiCount; i++) {
        const endpointKey = `onChangeApiEndpoint${i}` as keyof FormField;
        const endpoint = field[endpointKey] as string;

        if (endpoint) {
          // Replace {value} or {search_value} placeholder with actual search value
          let url = endpoint.replace(/{value}/g, encodeURIComponent(searchValue));
          url = url.replace(/{search_value}/g, encodeURIComponent(searchValue));
          const authToken = localStorage.getItem('authToken');
          const fullUrl = url.startsWith('http') ? url : `${WEB_API_BASE_URL}${url.startsWith('/') ? url : '/' + url}`;

          const headers: HeadersInit = {
            'Content-Type': 'application/json'
          };
          if (authToken) {
            headers['Authorization'] = `Bearer ${authToken}`;
          }

          apiCalls.push(
            fetch(fullUrl, {
              method: 'GET',
              headers
            }).then(res => res.json())
          );
        }
      }

      // Execute all API calls in parallel
      const responses = await Promise.all(apiCalls);
      processMultipleSearchApiResponses(field, responses);

    } catch (error) {
      console.error('Error in multiple search API calls:', error);
      alert('Search failed. Please try again.');
    } finally {
      setSearchingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(field.fieldName);
        return newSet;
      });
    }
  };

  /**
   * Handle search button click for TextBoxWithSearch fields
   */
  const handleSearchButtonClick = async (field: FormField) => {
    const searchValue = formData[field.fieldName];

    if (!searchValue || searchValue.trim() === '') {
      alert('Please enter a search term first');
      return;
    }

    // Check if API endpoint is configured
    if (!field.onChangeApiEndpoint1) {
      alert('Search is not configured for this field.');
      return;
    }

    // Check if multiple APIs need to be called
    if (field.onChangeApiCount && field.onChangeApiCount > 1) {
      await performMultipleSearchApiCalls(field, searchValue.trim());
    } else {
      await performSearchApiCall(field, searchValue.trim());
    }
  };

  // ============================================
  // End of TextBoxWithSearch Helper Functions
  // ============================================

  // Helper function to get today's date in the correct format
  const getTodayDate = (fieldType: string): string => {
    const today = new Date();
    
    if (fieldType === 'datetime' || fieldType === 'datetime-local') {
      // Format: YYYY-MM-DDTHH:MM
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const hours = String(today.getHours()).padStart(2, '0');
      const minutes = String(today.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } else if (fieldType === 'date') {
      // Format: YYYY-MM-DD
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    return '';
  };

  /**
   * Helper function to compare values with type coercion
   * Handles string/number/boolean type mismatches from database
   */
  const compareValues = (actualValue: any, expectedValue: any, operator: string = 'equals'): boolean => {
    // Handle null/undefined cases
    if (actualValue === null || actualValue === undefined || actualValue === '') {
      // Only match if expected is also null/undefined/empty
      return expectedValue === null || expectedValue === undefined || expectedValue === '';
    }

    // Try to match types for comparison
    let actual = actualValue;
    let expected = expectedValue;

    // If expected is a string number and actual is a number (or vice versa), convert for comparison
    if (typeof actual === 'number' && typeof expected === 'string' && !isNaN(Number(expected))) {
      expected = Number(expected);
    } else if (typeof expected === 'number' && typeof actual === 'string' && !isNaN(Number(actual))) {
      actual = Number(actual);
    }

    // If expected is a string boolean and actual is a boolean (or vice versa), convert for comparison
    if (typeof actual === 'boolean' && typeof expected === 'string') {
      expected = expected.toLowerCase() === 'true' || expected === '1';
    } else if (typeof expected === 'boolean' && typeof actual === 'string') {
      actual = actual.toLowerCase() === 'true' || actual === '1';
    }

    // Perform comparison based on operator
    switch (operator) {
      case 'equals':
        return actual === expected;
      case 'not_equals':
        return actual !== expected;
      case 'contains':
        return Array.isArray(actual) ?
          actual.includes(expected) :
          String(actual).includes(String(expected));
      case 'greater_than':
        return Number(actual) > Number(expected);
      case 'less_than':
        return Number(actual) < Number(expected);
      case 'greater_than_or_equal':
        return Number(actual) >= Number(expected);
      case 'less_than_or_equal':
        return Number(actual) <= Number(expected);
      default:
        return actual === expected;
    }
  };

  /**
   * Check if a field should be visible based on parent field values
   * EXACT COPY of CocaCola UI shouldShowField logic with type-aware comparison
   */
  const isFieldVisible = (field: FormField): boolean => {
    // Hide specific fields that should not be displayed in the form
    const hiddenFieldIdentifiers = [
      'cv_mn_wk_ctr',
      'cv_sys_status',
      'cv_cde_assigne',
      'cv_tsm',
      'cv_route_number',
      'cv_channel',
      'cv_segmentation',
      'cv_in_scl',
      'cv_customer_cde_record_qty',
      'cv_ave_monthly_volume',
      'cv_size_dimension',
      'cv_sort_fld',
      'cv_technical_ident_no',
      'cv_sloc',
      'cv_pp',
      'cv_physical_status',
      'with_missing_cde_record',
      'opru_with_missing_cde_record'
    ];

    const normalizedFieldName = field.fieldName.toLowerCase();
    const shouldHideField = hiddenFieldIdentifiers.some(identifier => {
      const normalizedIdentifier = identifier.toLowerCase();

      if (normalizedFieldName.includes(normalizedIdentifier)) {
        return true;
      }

      if (normalizedIdentifier.startsWith('cv_')) {
        const baseIdentifier = normalizedIdentifier.slice(3);
        if (baseIdentifier && normalizedFieldName.includes(baseIdentifier)) {
          return true;
        }
      }

      return false;
    });

    if (shouldHideField) {
      return false;
    }

    // Always hide if field is not visible by default
    if (!field.isVisible) {
      return false;
    }

    // Check conditional logic - EXACTLY like Angular UI
    // Only apply parent field logic if BOTH parentFieldId AND parentFieldValue are set
    if (field.parentFieldId && field.parentFieldValue !== undefined && field.parentFieldValue !== null && field.parentFieldValue !== '') {
      // Get parent value directly from formData - EXACTLY like Angular UI
      const parentValue = formData[field.parentFieldId];

      // Use type-aware comparison to handle string/number/boolean mismatches
      const primaryConditionMet = compareValues(
        parentValue,
        field.parentFieldValue,
        field.conditionOperator || 'equals'
      );

      // Check secondary condition if exists - EXACTLY like Angular UI
      if (field.secondaryParentFieldId && field.secondaryParentFieldValue !== undefined) {
        const secondaryParentValue = formData[field.secondaryParentFieldId];

        // Use type-aware comparison for secondary condition too
        const secondaryConditionMet = compareValues(
          secondaryParentValue,
          field.secondaryParentFieldValue,
          'equals' // Secondary condition always uses equals
        );

        // Apply logical operator - EXACTLY like Angular UI
        if (field.conditionalLogicOperator === 'AND' || field.conditionalLogicOperator == undefined) {
          return primaryConditionMet && secondaryConditionMet;
        } else {
          return primaryConditionMet || secondaryConditionMet;
        }
      }

      return primaryConditionMet;
    }

    return true;
  };

  // ==================== REPEATING SECTIONS LOGIC ====================
  
  // Initialize repeating sections with min rows
  React.useEffect(() => {
    const initialData: { [sectionId: string]: any[] } = {};
    
    repeatingSections.forEach(section => {
      initialData[section.sectionId] = [];
      
      // Initialize with min rows if specified
      if (section.minRows > 0 && section.triggerType !== 'quantity') {
        for (let i = 0; i < section.minRows; i++) {
          initialData[section.sectionId].push(createEmptyRow(section, i));
        }
      }
    });
    
    setRepeatingSectionData(initialData);
  }, [repeatingSections]);

  // Handle quantity trigger - auto-create rows based on trigger field value
  React.useEffect(() => {
    repeatingSections.forEach(section => {
      if (section.triggerType === 'quantity' && section.triggerFieldId) {
        const triggerValue = formData[section.triggerFieldId];
        const quantity = Number(triggerValue) || 0;
        const currentCount = repeatingSectionData[section.sectionId]?.length || 0;
        
        if (quantity !== currentCount && quantity >= 0) {
          updateRepeatingSectionRows(section, quantity);
        }
      }
    });
  }, [formData, repeatingSections, repeatingSectionData]);

  const updateRepeatingSectionRows = (section: RepeatingSectionType, targetCount: number) => {
    const currentData = repeatingSectionData[section.sectionId] || [];
    const currentCount = currentData.length;
    
    if (targetCount === currentCount) return;
    
    const newData = [...currentData];
    
    if (targetCount > currentCount) {
      // Add rows
      for (let i = currentCount; i < targetCount; i++) {
        const newRow = createEmptyRow(section, i);
        newData.push(newRow);
      }
    } else {
      // Remove rows
      newData.splice(targetCount);
    }
    
    setRepeatingSectionData(prev => ({
      ...prev,
      [section.sectionId]: newData
    }));
  };

  const handleAddRow = (sectionId: string) => {
    const section = repeatingSections.find(s => s.sectionId === sectionId);
    if (!section) return;
    
    const currentData = repeatingSectionData[sectionId] || [];
    if (currentData.length >= section.maxRows) {
      alert(`Maximum ${section.maxRows} rows allowed`);
      return;
    }
    
    const newRow = createEmptyRow(section, currentData.length);
    setRepeatingSectionData(prev => ({
      ...prev,
      [sectionId]: [...currentData, newRow]
    }));
  };

  const handleRemoveRow = (sectionId: string, rowIndex: number) => {
    const section = repeatingSections.find(s => s.sectionId === sectionId);
    if (!section) return;
    
    const currentData = repeatingSectionData[sectionId] || [];
    if (currentData.length <= section.minRows) {
      alert(`Minimum ${section.minRows} rows required`);
      return;
    }
    
    const newData = currentData.filter((_, index) => index !== rowIndex);
    // Update row indices
    newData.forEach((row, index) => {
      row._rowIndex = index;
    });
    
    setRepeatingSectionData(prev => ({
      ...prev,
      [sectionId]: newData
    }));
  };

  const handleRepeatingSectionFieldChange = (sectionId: string, rowIndex: number, fieldName: string, value: any) => {
    setRepeatingSectionData(prev => {
      const newData = { ...prev };
      if (!newData[sectionId]) newData[sectionId] = [];
      if (!newData[sectionId][rowIndex]) newData[sectionId][rowIndex] = {};
      newData[sectionId][rowIndex][fieldName] = value;
      return newData;
    });
  };

  // Expose repeating section data to parent via onChange
  React.useEffect(() => {
    // Store repeating section data in a special key so parent can access it
    if (Object.keys(repeatingSectionData).length > 0) {
      onChange('_repeatingSectionData', repeatingSectionData);
    }
  }, [repeatingSectionData]);

// ==================== END REPEATING SECTIONS LOGIC ====================

const renderField = (field: FormField) => {

  // For date fields, use today's date as default if no value is set
  if (field.fieldType === 'date' || field.fieldType === 'datetime' || field.fieldType === 'datetime-local') {
    if (!formData[field.fieldName] && !field.defaultValue) {
      const todayValue = getTodayDate(field.fieldType);
      if (todayValue) {
        onChange(field.fieldName, todayValue);
      }
    }
  }

  // Check if field should be visible based on parent field values
  if (!isFieldVisible(field)) {
    return null;
  }

  // Get the current value (including any default that was just set)
  let value = formData[field.fieldName] || field.defaultValue || '';

  const hasError = !!mergedErrors[field.fieldName];
  const isLoading = loadingFields.has(field.fieldName);

  // Get options for dropdowns and radios
  const getOptions = (): FieldOption[] => {
    // First check if we have loaded options (from API or static)
    if (dropdownOptions[field.fieldName]) {
      return dropdownOptions[field.fieldName];
    }

    // Fallback to static options if available
    if (field.staticOptions && Array.isArray(field.staticOptions)) {
      return field.staticOptions;
    }

    return [];
  };

  switch (field.fieldType) {
      case 'h2':
      case 'H2':
        // H2 is a heading/label, not an input field
        return <H2Heading>{field.label || field.fieldName}</H2Heading>;

      case 'p':
      case 'P':
      case 'paragraph':
      case 'Paragraph':
        // Paragraph is a display-only field showing informational/consent text
        // The text content comes from field.placeholder or field.label
        const paragraphText = field.placeholder || field.label || '';
        return <ParagraphDisplay>{paragraphText}</ParagraphDisplay>;

      case 'textboxwithsearch':
      case 'TextBoxWithSearch':
        return (
          <TextBoxWithSearchWrapper>
            <SearchInput
              type="text"
              value={value}
              onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
              placeholder={field.placeholder || 'Enter search value'}
              disabled={field.isReadOnly || !field.isEditable || searchingFields.has(field.fieldName)}
              className={hasError ? 'error' : ''}
            />
            <SearchButton
              type="button"
              onClick={() => handleSearchButtonClick(field)}
              disabled={field.isReadOnly || !field.isEditable || searchingFields.has(field.fieldName)}
              isSearching={searchingFields.has(field.fieldName)}
            >
              {searchingFields.has(field.fieldName) ? (
                <>
                  <FaSpinner />
                  Searching...
                </>
              ) : (
                <>
                  <FaSearch />
                  Search
                </>
              )}
            </SearchButton>
          </TextBoxWithSearchWrapper>
        );

      case 'text':
      case 'email':
      case 'tel':
      case 'url':
        // Check if this is the contact number field that needs country code selector
        if (field.fieldName === 'contact_no_one' ||
            field.fieldName.toLowerCase().includes('contactno') ||
            field.fieldName.toLowerCase().includes('contact_no')) {
          return (
            <PhoneInput
              value={value || ''}
              onChange={(phoneValue) => handleFieldChange(field.fieldName, phoneValue)}
              onBlur={() => {
                // Handle serial number binding if needed
                if (value) {
                  handleSerialNumberBinding(value, field.fieldName);
                }
              }}
              disabled={field.isReadOnly || !field.isEditable}
              // Don't pass error here - DynamicFormRenderer handles error display via ErrorText
              placeholder={field.placeholder || 'Enter phone number'}
              defaultCountry="PH"
            />
          );
        }

        // Check if this text field has an API endpoint configured for auto-population
        const textFieldHasApi = field.onChangeApiEndpoint1 && field.onChangeApiEndpoint1.trim() !== '';

        return (
          <Input
            type={field.fieldType}
            value={value}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            onBlur={(e) => {
              const inputValue = e.target.value;
              // Handle serial number binding on blur for any serial number field
              handleSerialNumberBinding(inputValue, field.fieldName);

              // If this text field has an API endpoint configured, trigger the search
              if (textFieldHasApi && inputValue && inputValue.trim() !== '') {
                console.log(`🔍 Text field ${field.fieldName} has API configured, triggering search on blur...`);
                if (field.onChangeApiCount && field.onChangeApiCount > 1) {
                  performMultipleSearchApiCalls(field, inputValue.trim());
                } else {
                  performSearchApiCall(field, inputValue.trim());
                }
              }
            }}
            onPaste={(e) => {
              // Handle serial number binding on paste (with slight delay to ensure value is set)
              setTimeout(() => {
                const input = e.target as HTMLInputElement;
                handleSerialNumberBinding(input.value, field.fieldName);

                // If this text field has an API endpoint configured, trigger the search on paste
                if (textFieldHasApi && input.value && input.value.trim() !== '') {
                  console.log(`🔍 Text field ${field.fieldName} has API configured, triggering search on paste...`);
                  if (field.onChangeApiCount && field.onChangeApiCount > 1) {
                    performMultipleSearchApiCalls(field, input.value.trim());
                  } else {
                    performSearchApiCall(field, input.value.trim());
                  }
                }
              }, 100);
            }}
            placeholder={field.placeholder}
            disabled={field.isReadOnly || !field.isEditable}
            className={hasError ? 'error' : ''}
            maxLength={field.maxLength}
            minLength={field.minLength}
          />
        );

      case 'qrscanner':
        // Check if this field has search API configured (BarcodeScanner with search)
        const hasSearchApi = field.onChangeApiEndpoint1 && field.onChangeApiEndpoint1.trim() !== '';
        
        if (hasSearchApi) {
          // Render as Input + Scanner + Search buttons (like TextBoxWithSearch)
          return (
            <>
              <TextBoxWithSearchWrapper>
                <InputGroup style={{ flex: 1, position: 'relative' }}>
                  <Input
                    type="text"
                    value={value}
                    onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                    placeholder={field.placeholder || 'Enter or scan serial number'}
                    disabled={field.isReadOnly || !field.isEditable || searchingFields.has(field.fieldName) || scanningField === field.fieldName}
                    className={hasError ? 'error' : ''}
                    style={{ paddingRight: '50px', width: '100%' }}
                  />
                  <IconButton
                    onClick={() => startScanning(field.fieldName)}
                    disabled={field.isReadOnly || !field.isEditable || scanningField === field.fieldName}
                    title="Scan Barcode"
                  >
                    <FaQrcode size={18} />
                  </IconButton>
                </InputGroup>
                <SearchButton
                  type="button"
                  onClick={() => handleSearchButtonClick(field)}
                  disabled={field.isReadOnly || !field.isEditable || searchingFields.has(field.fieldName)}
                  isSearching={searchingFields.has(field.fieldName)}
                >
                  {searchingFields.has(field.fieldName) ? (
                    <>
                      <FaSpinner />
                      Searching...
                    </>
                  ) : (
                    <>
                      <FaSearch />
                      Search
                    </>
                  )}
                </SearchButton>
              </TextBoxWithSearchWrapper>
              
              {/* Scanner Overlay Popup */}
              <ScannerOverlay isVisible={scanningField === field.fieldName}>
                <ScannerBox>
                  <ScannerTitle>Scanning QR Code...</ScannerTitle>
                  <BetterBarcodeScanner
                    isActive={scanningField === field.fieldName}
                    onScan={(result) => {
                      console.log('Barcode scanned:', result);
                      if (result && result.trim()) {
                        const trimmedResult = result.trim();
                        onChange(field.fieldName, trimmedResult);
                        stopScanning();

                        // Automatically trigger API search after scanning (like clicking Search button)
                        if (field.onChangeApiEndpoint1) {
                          console.log('🔍 Scanner: Auto-triggering API search for:', trimmedResult);
                          setTimeout(() => {
                            if (field.onChangeApiCount && field.onChangeApiCount > 1) {
                              performMultipleSearchApiCalls(field, trimmedResult);
                            } else {
                              performSearchApiCall(field, trimmedResult);
                            }
                          }, 300);
                        }

                        if (onScan) {
                          onScan(field.fieldName, trimmedResult);
                        }
                      }
                    }}
                    onError={(error) => {
                      console.error('Scanner error:', error);
                      alert('Scanner error: ' + error.message);
                      stopScanning();
                    }}
                    onCancel={stopScanning}
                  />
                </ScannerBox>
              </ScannerOverlay>
            </>
          );
        }
        
        // Regular QR scanner without search
        return (
          <>
            <InputGroup>
              <Input
                type="text"
                value={value}
                onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                onBlur={(e) => {
                  // Handle serial number binding on blur for any serial number field
                  handleSerialNumberBinding(e.target.value, field.fieldName);
                }}
                onPaste={(e) => {
                  // Handle serial number binding on paste (with slight delay to ensure value is set)
                  setTimeout(() => {
                    const input = e.target as HTMLInputElement;
                    handleSerialNumberBinding(input.value, field.fieldName);
                  }, 100);
                }}
                placeholder={isBindingSerialNumber ? 'Loading equipment data...' : (field.placeholder || 'Enter or scan')}
                disabled={field.isReadOnly || !field.isEditable || scanningField === field.fieldName || isBindingSerialNumber}
                className={hasError ? 'error' : ''}
                style={{ paddingRight: '50px' }}
              />
              <IconButton
                onClick={() => startScanning(field.fieldName)}
                disabled={field.isReadOnly || !field.isEditable || scanningField === field.fieldName || isBindingSerialNumber}
                title={isBindingSerialNumber ? "Loading..." : "Scan QR Code"}
                style={{ animation: isBindingSerialNumber ? 'spin 1s linear infinite' : 'none' }}
              >
                {isBindingSerialNumber ? <FaSpinner size={18} /> : <FaQrcode size={18} />}
              </IconButton>
            </InputGroup>
            
            {/* Scanner Overlay Popup */}
            <ScannerOverlay isVisible={scanningField === field.fieldName}>
              <ScannerBox>
                <ScannerTitle>Scanning QR Code...</ScannerTitle>
                <BetterBarcodeScanner
                  isActive={scanningField === field.fieldName}
                  onScan={(result) => {
                    console.log('Barcode scanned:', result);
                    if (result && result.trim()) {
                      onChange(field.fieldName, result);
                      stopScanning();
                      
                      if (onScan) {
                        onScan(field.fieldName, result);
                      }
                    }
                  }}
                  onError={(error) => {
                    console.error('Scanner error:', error);
                    alert('Scanner error: ' + error.message);
                    stopScanning();
                  }}
                  onCancel={stopScanning}
                />
              </ScannerBox>
            </ScannerOverlay>
          </>
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.isReadOnly || !field.isEditable}
            className={hasError ? 'error' : ''}
            min={field.minValue}
            max={field.maxValue}
          />
        );

      case 'textarea':
        return (
          <TextArea
            value={value}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.isReadOnly || !field.isEditable}
            className={hasError ? 'error' : ''}
            maxLength={field.maxLength}
            minLength={field.minLength}
          />
        );

      case 'date':
      case 'datetime':
      case 'datetime-local':
        // Determine the actual HTML input type that will be rendered
        const actualInputType = field.fieldType === 'datetime' ? 'datetime-local' : field.fieldType;
        
        // Ensure value format matches the input type
        let dateValue = value;
        if (dateValue && typeof dateValue === 'string') {
          if (actualInputType === 'date' && dateValue.includes('T')) {
            // Input expects YYYY-MM-DD but value has time component
            dateValue = dateValue.split('T')[0];
          } else if (actualInputType === 'datetime-local' && !dateValue.includes('T')) {
            // Input expects YYYY-MM-DDTHH:MM but value is just date
            dateValue = dateValue + 'T00:00';
          }
        }
        
        return (
          <InputGroup>
            <Input
              type={actualInputType}
              value={dateValue}
              onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
              disabled={field.isReadOnly || !field.isEditable}
              className={hasError ? 'error' : ''}
              min={field.minValue}
              max={field.maxValue || '2099-12-31'}
              onClick={(e) => {
                // Open date picker on click anywhere in the input
                const input = e.target as HTMLInputElement;
                if (input.showPicker) {
                  input.showPicker();
                }
              }}
            />
            <CalendarIcon>
              <FaCalendar />
            </CalendarIcon>
          </InputGroup>
        );

      case 'dropdown':
      case 'dropdownpopup':
      case 'select':
        return (
          <ModernPopupDropdown
            value={value}
            onChange={(newValue) => handleFieldChange(field.fieldName, newValue)}
            options={getOptions()}
            placeholder={isLoading ? 'Loading...' : `Select ${field.label}`}
            disabled={field.isReadOnly || !field.isEditable || isLoading}
            required={field.isMandatory}
            hasError={hasError}
            fieldName={field.fieldName}
            label={field.label}
          />
        );
      case 'multiselect':
        return (
          <MultiSelectField
            value={value || []}
            onChange={(values) => handleFieldChange(field.fieldName, values)}
            options={getOptions()}
            placeholder={isLoading ? 'Loading...' : `Select ${field.label}`}
            disabled={field.isReadOnly || !field.isEditable || isLoading}
            required={field.isMandatory}
            hasError={hasError}
          />
        );

      case 'checkbox':
        return (
          <CheckboxWrapper>
            <Checkbox
              type="checkbox"
              checked={value === true || value === 'true' || value === '1'}
              onChange={(e) => handleFieldChange(field.fieldName, e.target.checked)}
              disabled={field.isReadOnly || !field.isEditable}
            />
            <span>{field.label}</span>
          </CheckboxWrapper>
        );

      case 'radio':
        return (
          <RadioGroup>
            {getOptions().map((option) => (
              <RadioWrapper key={option.value}>
                <input
                  type="radio"
                  name={field.fieldName}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
                  disabled={field.isReadOnly || !field.isEditable}
                />
                <span>{option.text}</span>
              </RadioWrapper>
            ))}
          </RadioGroup>
        );

      case 'location':
        // Check if map selection should be enabled (default: true, can be disabled via field config)
        const enableMapSelection = field.allowedFileTypes !== 'gps-only'; // Use allowedFileTypes as flag for now
        
        return (
          <MapLocationPicker
            value={value}
            onChange={(locationString, lat, lng) => {
              onChange(field.fieldName, locationString);
              
              // Also update individual lat/lng fields if they exist
              if (onLocationCapture) {
                onLocationCapture(lat, lng);
              }
            }}
            disabled={field.isReadOnly || !field.isEditable}
            hasError={hasError}
            enableMapSelection={enableMapSelection}
          />
        );

      case 'fileupload':
      case 'Image Uploader':
        const renderImagePreviews = () => {
          if (!value) return null;

          const files = Array.isArray(value) ? value : [value];
          const isDisabled = field.isReadOnly || !field.isEditable;

          return (
            <ImagePreviewContainer>
              {files.map((file: File, index: number) => {
                const previewUrl = file instanceof File ? URL.createObjectURL(file) : file;

                return (
                  <ImagePreviewItem key={index}>
                    <ClickableImagePreview
                      src={previewUrl}
                      alt={`Preview ${index + 1}`}
                      onClick={() => {
                        setSelectedImage(previewUrl);
                        setIsImageModalOpen(true);
                      }}
                    />
                    {/* Only show remove button if field is not disabled */}
                    {!isDisabled && (
                      <RemoveImageButton
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          if (Array.isArray(value)) {
                            const newFiles = value.filter((_: any, i: number) => i !== index);
                            onChange(field.fieldName, newFiles.length > 0 ? newFiles : null);
                          } else {
                            onChange(field.fieldName, null);
                          }
                        }}
                      >
                        ×
                      </RemoveImageButton>
                    )}
                  </ImagePreviewItem>
                );
              })}
            </ImagePreviewContainer>
          );
        };
        
        return (
          <>
            <FileUploadLabel htmlFor={`file-${field.fieldName}`}>
              <FaCamera />
              <span>{value ? 'Add More Images' : 'Upload Image'}</span>
            </FileUploadLabel>
            <HiddenFileInput
              id={`file-${field.fieldName}`}
              type="file"
              accept={field.fieldType === 'Image Uploader' ? 'image/*' : (field.allowedFileTypes || '*')}
              capture={field.fieldType === 'Image Uploader' ? 'environment' : undefined}
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  // Append new files to existing ones
                  const existingFiles = Array.isArray(value) ? value : (value ? [value] : []);
                  onChange(field.fieldName, [...existingFiles, ...files]);
                }
              }}
              disabled={field.isReadOnly || !field.isEditable}
            />
            {renderImagePreviews()}
            {value && (
              <HelpText>
                {Array.isArray(value) 
                  ? `${value.length} image(s) selected` 
                  : `Image selected: ${value.name || value}`
                }
              </HelpText>
            )}
          </>
        );

      case 'camera':
        const renderCameraPreview = () => {
          if (!value) return null;

          const files = Array.isArray(value) ? value : [value];
          const isDisabled = field.isReadOnly || !field.isEditable;

          return (
            <ImagePreviewContainer>
              {files.map((file: File, index: number) => {
                const previewUrl = file instanceof File ? URL.createObjectURL(file) : file;

                return (
                  <ImagePreviewItem key={index}>
                    <ClickableImagePreview
                      src={previewUrl}
                      alt={`Preview ${index + 1}`}
                      onClick={() => {
                        setSelectedImage(previewUrl);
                        setIsImageModalOpen(true);
                      }}
                    />
                    {/* Only show remove button if field is not disabled */}
                    {!isDisabled && (
                      <RemoveImageButton
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          if (Array.isArray(value)) {
                            const newFiles = value.filter((_: any, i: number) => i !== index);
                            onChange(field.fieldName, newFiles.length > 0 ? newFiles : null);
                          } else {
                            onChange(field.fieldName, null);
                          }
                        }}
                      >
                        ×
                      </RemoveImageButton>
                    )}
                  </ImagePreviewItem>
                );
              })}
            </ImagePreviewContainer>
          );
        };
        
        return (
          <>
            <FileUploadLabel htmlFor={`camera-${field.fieldName}`}>
              <FaCamera />
              <span>{value ? 'Add More Photos' : 'Take Photo'}</span>
            </FileUploadLabel>
            <HiddenFileInput
              id={`camera-${field.fieldName}`}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  // Append new files to existing ones
                  const existingFiles = Array.isArray(value) ? value : (value ? [value] : []);
                  onChange(field.fieldName, [...existingFiles, ...files]);
                }
              }}
              disabled={field.isReadOnly || !field.isEditable}
            />
            {renderCameraPreview()}
            {value && Array.isArray(value) && (
              <HelpText>
                {`${value.length} photo(s) selected`}
              </HelpText>
            )}
          </>
        );

            case 'Image Gallery':
        return (
          <>
            <FileUploadLabel htmlFor={`gallery-${field.fieldName}`}>
              <FaCamera />
              <span>{value ? 'Change Images' : 'Select Images'}</span>
            </FileUploadLabel>
            <HiddenFileInput
              id={`gallery-${field.fieldName}`}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  // Store File objects - formSubmissionService will convert to base64
                  onChange(field.fieldName, files);
                }
              }}
              disabled={field.isReadOnly || !field.isEditable}
            />
            {value && (
              <HelpText>
                {(() => {
                  let count = 0;
                  if (typeof value === 'string' && value.includes('|')) {
                    count = value.split('|').filter(Boolean).length;
                  } else if (Array.isArray(value)) {
                    count = value.length;
                  } else if (value) {
                    count = 1;
                  }
                  return `${count} image(s) selected`;
                })()}
              </HelpText>
            )}
          </>
        );

      case 'signature':
      case 'signaturepad':
      case 'SignaturePad':
        return (
          <SignaturePad
            value={value}
            onChange={(signatureData) => handleFieldChange(field.fieldName, signatureData)}
            disabled={field.isReadOnly || !field.isEditable}
            hasError={hasError}
          />
        );

      case 'agreementtemplate':
      case 'AgreementTemplate':
        return (
          <AgreementTemplate
            fieldId={field.fieldName}
            value={value === true || value === 'true' || value === '1'}
            onChange={(checked) => handleFieldChange(field.fieldName, checked)}
            formData={formData}
            disabled={field.isReadOnly || !field.isEditable}
            hasError={hasError}
          />
        );

      case 'button':
      case 'Button':
        // Button fields are special - they don't have values, they trigger actions
        // For now, we'll handle them in the parent component (DynamicServiceRequest)
        // Don't render anything here, the button will be rendered separately
        return null;

      default:
        return (
          <Input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.fieldName, e.target.value)}
            placeholder={field.placeholder}
            disabled={field.isReadOnly || !field.isEditable}
            className={hasError ? 'error' : ''}
          />
        );
    }
  };

  // Render by sections
  const renderSections = () => {
    const sectionElements: JSX.Element[] = [];

    sections.forEach((sectionFields, sectionName) => {
      // Filter out fields that are not visible based on parent field logic
      // AND filter out fields that belong to repeating sections (they'll be rendered separately)
      const visibleFields = sectionFields.filter(field => 
        isFieldVisible(field) && !field.isRepeatingSection
      );

      // Skip sections with no visible fields
      if (visibleFields.length === 0) return;

      sectionElements.push(
        <FormSection key={sectionName}>
          {sectionName && sectionName !== 'General' && (
            <SectionTitle>{sectionName}</SectionTitle>
          )}
          <FormGrid columns={2}>
            {visibleFields.map((field) => {

              return (
                <FormGroup key={field.fieldName} columnSpan={field.columnSpan}>
                  {/* Don't show label for checkbox, H2 (heading), and paragraph (P) fields */}
                  {field.fieldType !== 'checkbox' &&
                   field.fieldType !== 'h2' &&
                   field.fieldType !== 'H2' &&
                   field.fieldType !== 'p' &&
                   field.fieldType !== 'P' &&
                   field.fieldType !== 'paragraph' &&
                   field.fieldType !== 'Paragraph' && (
                    <Label>
                      {field.label}
                      {field.isMandatory && <RequiredMark>*</RequiredMark>}
                    </Label>
                  )}
                  {renderField(field)}
                  {field.helpText && !mergedErrors[field.fieldName] && (
                    <HelpText>{field.helpText}</HelpText>
                  )}
                  {mergedErrors[field.fieldName] && (
                    <ErrorText>{mergedErrors[field.fieldName]}</ErrorText>
                  )}
                </FormGroup>
              );
            })}
          </FormGrid>
        </FormSection>
      );
    });

    // Render repeating sections (detail tables)
    repeatingSections.forEach(section => {
      // Skip if section is not visible based on trigger conditions
      if (!isSectionVisible(section, formData)) {
        return;
      }

      // Filter out fields that are part of repeating sections from regular rendering
      // They will be rendered inside the RepeatingSection component
      const rows = repeatingSectionData[section.sectionId] || [];

      sectionElements.push(
        <RepeatingSection
          key={section.sectionId}
          section={section}
          rows={rows}
          formData={formData}
          onAddRow={() => handleAddRow(section.sectionId)}
          onRemoveRow={(rowIndex) => handleRemoveRow(section.sectionId, rowIndex)}
          onFieldChange={(rowIndex, fieldName, value) => 
            handleRepeatingSectionFieldChange(section.sectionId, rowIndex, fieldName, value)
          }
        />
      );
    });

    return sectionElements;
  };

  return (
    <>
      {renderSections()}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ isVisible: false, message: '', type: 'loading' })}
      />

      {/* Full-screen Image Modal */}
      <ImageModalOverlay
        isOpen={isImageModalOpen}
        onClick={() => {
          setIsImageModalOpen(false);
          setSelectedImage(null);
        }}
      >
        {selectedImage && (
          <>
            <ModalImage
              src={selectedImage}
              alt="Full screen preview"
              onClick={(e) => e.stopPropagation()}
            />
            <CloseModalButton
              onClick={() => {
                setIsImageModalOpen(false);
                setSelectedImage(null);
              }}
            >
              ×
            </CloseModalButton>
          </>
        )}
      </ImageModalOverlay>
    </>
  );
};

export default DynamicFormRenderer;